import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/common/Modal';
import { UserRole, Employee } from '../../types';
import { CreateUserDto, UpdateUserDto, usersService, UserDetail } from '../../services/api/users';
import { employeesService } from '../../services/api/employees';
import { useToast } from '../../contexts/ToastContext';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user?: UserDetail | null;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const isEditMode = !!user;

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const [formData, setFormData] = useState({
    email: '',
    role: 'EMPLOYEE' as UserRole,
    employeeId: '',
    tempPassword: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        const emps = await employeesService.getAll({ pageSize: 1000, status: 'ACTIVE' as any });
        setEmployees(emps.items);
      } catch (err) {
        showError('Failed to load employees');
      } finally {
        setLoadingData(false);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        role: user.role,
        employeeId: user.employeeId ? String(user.employeeId) : '',
        tempPassword: '',
        isActive: user.isActive,
      });
    } else {
      setFormData({
        email: '',
        role: 'EMPLOYEE' as UserRole,
        employeeId: '',
        tempPassword: '',
        isActive: true,
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = t('errors.validation') + ': Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.validation') + ': Invalid email format';
    }
    if (!formData.role) {
      newErrors.role = t('errors.validation') + ': Role is required';
    }
    if (!isEditMode && formData.tempPassword && formData.tempPassword.length < 8) {
      newErrors.tempPassword = t('errors.validation') + ': Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      if (isEditMode && user) {
        const updateData: UpdateUserDto = {
          email: formData.email,
          role: formData.role,
          employeeId: formData.employeeId ? Number(formData.employeeId) : null,
          isActive: formData.isActive,
        };
        await usersService.update(user.id, updateData);
        showSuccess(t('users.edit') + ' - ' + t('common.success'));
      } else {
        const createData: CreateUserDto = {
          email: formData.email,
          role: formData.role,
          employeeId: formData.employeeId ? Number(formData.employeeId) : undefined,
          tempPassword: formData.tempPassword || undefined,
          isActive: formData.isActive,
        };
        await usersService.create(createData);
        showSuccess(t('users.create') + ' - ' + t('common.success'));
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Operation failed';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const roles = Object.values(UserRole);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('users.edit') : t('users.create')}
      size="medium"
    >
      {loadingData ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>{t('common.loading')}</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('users.email')} *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="user@company.com"
            />
            {errors.email && <div className="form-error">{errors.email}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('users.role')} *</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={errors.role ? 'error' : ''}
              >
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {t(`roles.${role.toLowerCase()}`)}
                  </option>
                ))}
              </select>
              {errors.role && <div className="form-error">{errors.role}</div>}
            </div>
            <div className="form-group">
              <label>{t('users.employee')}</label>
              <select name="employeeId" value={formData.employeeId} onChange={handleChange}>
                <option value="">-- No Employee Link --</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.fullName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!isEditMode && (
            <div className="form-group">
              <label>Temporary Password</label>
              <input
                type="password"
                name="tempPassword"
                value={formData.tempPassword}
                onChange={handleChange}
                className={errors.tempPassword ? 'error' : ''}
                placeholder="Leave empty to send password reset email"
              />
              {errors.tempPassword && <div className="form-error">{errors.tempPassword}</div>}
              <small style={{ color: '#666', fontSize: '12px' }}>
                If not set, user will receive a password reset link via email.
              </small>
            </div>
          )}

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              {t('users.isActive')}
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="button button-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="button button-primary" disabled={loading}>
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default UserFormModal;
